<template>
    <div class="c-date-picker" :class="'text-'+color">
        <flat-pickr v-model="getDate" :config="config"></flat-pickr>
        <button class="btn-nav prev" :disabled="isPrevDisabled" :class="color" @click.prevent="clickPrevDate"></button>
        <button class="btn-nav next" :class="color" @click.prevent="clickNextDate"></button>
        <div class="date" :class="color">{{formatDate}}</div>
    </div>
</template>

<script>
import {areDatesEqual, getWeekDay} from "@/utils/utils";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import FormatDateMixin from "@/mixins/FormatDateMixin";
import moment from "moment";
import {Dutch} from "flatpickr/dist/l10n/nl.js";
import {French} from "flatpickr/dist/l10n/fr.js";
import {German} from "flatpickr/dist/l10n/de.js";
import {english} from "flatpickr/dist/l10n/default.js";

export default {
    name: "DatePicker",
    components: {flatPickr},
    mixins : [FormatDateMixin],
    data() {
        return {
            config : {
                dateFormat: "Y-m-d",
                disable: [
                    date => {
                        if (this.disabledDays.includes(getWeekDay(date))) {
                            return true;
                        }
                        if (this.lockPast) {
                            const today = new Date();
                            return date < today && !areDatesEqual(date, today);
                        }
                    }
                ],
                locale: Dutch
            }
        };
    },
    props: {
        date: {
            type: String, default: ""
        },
        lockPast : {
            type: Boolean, default : false
        },
        color : {
            type:String, default:"red"
        },
        disabledDays : {
            type:Array, default: () => []
        }
    },

    computed: {
        getDate: {
            get: function() {
                return this.date;
            },
            set: function(value) {
                this.$emit("update:date", value);
            }
        },

        formatDate() {
            return this.fullFormatDate(this.getDate);
        },

        isPrevDisabled() {
            return this.lockPast && this.getDate < moment().add(1, "days").format("YYYY-MM-DD");
        }
    },

    watch: {
        "$i18n.locale": {
            immediate: true,
            deep: true,
            handler() {
                this.setDateLocale();
            }
        }
    },

    methods: {
        clickPrevDate() {
            let m;
            let prevDate = this.getDate;
            do {
                m = moment(prevDate, "YYYY-MM-DD").subtract(1, "days");
                prevDate = m.format("YYYY-MM-DD");
            } while (this.disabledDays.includes(getWeekDay(m.toDate())));
            this.getDate = m.format("YYYY-MM-DD");
        },

        clickNextDate() {
            let m;
            let nextDate = this.getDate;
            do {
                m = moment(nextDate, "YYYY-MM-DD").add(1, "days");
                nextDate = m.format("YYYY-MM-DD");
            } while (this.disabledDays.includes(getWeekDay(m.toDate())));
            this.getDate = m.format("YYYY-MM-DD");
        },

        setDateLocale() {
            const locales = {
                benl : Dutch,
                nl : Dutch,
                fr : French,
                de: German,
                en : english
            };

            this.config.locale = locales[this.$i18n.locale];
        }
    }
};
</script>

<style lang="scss" scoped>
    .c-date-picker {
        padding-left : 4.5rem;
        position: relative;
        height: 2.5rem;
        line-height: 2.5rem;
        display: flex;
        align-items: center;
        @include fadeIntro(350ms,100ms);

        &::before {
            content : ' ';
            position: absolute;
            background-image: url('/img/header-lines.svg');
            background-repeat: no-repeat;
            background-size: 100%;
            width: 4rem;
            height: 2.5rem;
            left: 0;
            top: 0.85rem;
        }

        .date {
            font-size: 1.25em;
            border: none;
            height: auto;
            margin: .55em 0;
            padding: 0;
            background : transparent;
            outline : none;
            text-transform: capitalize;
            overflow: hidden;
            white-space: nowrap;
            margin-right : 1rem;

            &.green {
                color : $color-green;
            }
            &.yellow {
                color : $color-yellow;
            }
            &.blue {
                color : $color-blue;
            }
        }

        input {
            opacity: 0;
            position: absolute;
            width: 100%;
            height: 100%;
       }

       .btn-nav {
            background : transparent;
            border: none;

            &.prev {
                transform: scale(-1, 1);
            }
            &.green {
                background-image : url('/img/btn-arrow-green.svg');
            }
            &.yellow {
                background-image : url('/img/btn-arrow-yellow.svg');
            }
            &.blue {
                background-image : url('/img/btn-arrow-blue.svg');
            }
            background-repeat :no-repeat;
            width: 1.75rem;
            height: 1.75rem;
            margin-right: 0.2rem;
            margin-top: -.2rem;
            position: relative;
            z-index: 5;

            &:disabled {
                opacity: 0.5;
            }
       }
   }
</style>